import { Table, TableHeader, TableRow } from 'component/Table';
import { ButtonComponent } from 'component/Button';
import Dialog from '@mui/material/Dialog';
import 'assets/styles/pages/donors.scss';
import 'assets/styles/pages/organizations.scss';
import { React, useEffect, useState } from 'react';
import { ReactComponent as ViewIcon } from 'assets/images/icons/Eye-on.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/mode_edit_24px.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/icons/delete_24px.svg';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { axiosWrapper, getPageValue } from 'utils/Helper';
import { getCustomers, deleteCustomer } from 'api/CustomersApi';
import { toast } from 'react-toastify';
import { useUser } from 'routes/UserContext';
import { NewCustomer } from './NewCustomer';
import { CustomerDialog } from './CustomerDialog';

export const Customers = ({ setLoader }) => {
  const [customerName, setCustomerName] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const { user } = useUser();

  const canManage = user?.permissions?.manage_task_customers;

  const getCustomersList = (pageAfterCreate = false) => {
    axiosWrapper(() => {
      const customerPage = pageAfterCreate ? pageAfterCreate : page;
      getCustomers(customerPage)
        .then((res) => {
          const results = res?.data?.results ?? res?.data;
          const nextUrl = res?.data?.next;
          if (pageAfterCreate) {
            setCustomers(Array.isArray(results) ? results : []);
          } else {
            setCustomers((prev) => (Array.isArray(results) ? [...prev, ...results] : prev));
          }
          setNext(!!nextUrl);
          if (nextUrl) {
            setPage(Number(getPageValue(nextUrl)));
          }
          setLoader(false);
        })
        .catch((err) => {
          console.error(err);
          toast.error('Customers are not loaded');
          setCustomers([]);
          setLoader(false);
        });
    });
  };

  const handleDelete = (customer) => {
    axiosWrapper(() => {
      deleteCustomer(customer?.id)
        .then(() => {
          toast.success('Customer deleted');
          const deletedCustomerId = customer?.id;
          setCustomers((prev) =>
            prev.filter((customerItem) => customerItem?.id !== deletedCustomerId)
          );
        })
        .catch((err) => {
          const msg = err?.response?.data?.detail || 'Cannot delete: customer is used in reports';
          toast.error(msg);
        });
    });
  };

  useEffect(() => {
    setLoader(true);
    getCustomersList();
  }, []);

  return (
    <div className="donors flex-column-group">
      <Table>
        <TableHeader>
          <th>Name</th>
          <th>Organizations</th>
          <th>Status</th>
          <th>Actions</th>
        </TableHeader>
        <tbody>
          {!customers.length && (
            <TableRow>
              <td colSpan={4}>Дані відсутні</td>
            </TableRow>
          )}
          {customers.map((customer, index) => (
            <TableRow key={customer?.id ?? index} className="org-row-compact">
              <td>{customer.name}</td>
              <td>
                {(customer?.organizations_detail ?? customer?.organizations)
                  ?.map(
                    (organization) => organization.name ?? organization.abbreviation ?? organization
                  )
                  .join(', ') || '-'}
              </td>
              <td>
                <span
                  className={`org-status-badge ${customer?.is_active ? 'org-status-active' : 'org-status-inactive'}`}
                >
                  {customer?.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="icons org-actions">
                <CustomerDialog
                  mode="view"
                  customerId={customer?.id}
                  triggerIcon={<ViewIcon className="view-icon" style={{ color: '#868686' }} />}
                />
                {canManage && (
                  <>
                    <CustomerDialog
                      mode="edit"
                      customerId={customer?.id}
                      onSuccess={() => getCustomersList(1)}
                      triggerIcon={<EditIcon className="edit-icon" style={{ color: '#868686' }} />}
                    />
                    <button
                      type="button"
                      className="icon-action-btn"
                      onClick={() => setCustomerToDelete(customer)}
                      title="Delete"
                    >
                      <DeleteIcon className="delete-icon" style={{ color: '#868686' }} />
                    </button>
                  </>
                )}
              </td>
            </TableRow>
          ))}
          {next && (
            <div className="loadMore-div">
              <button
                className="loadMore-button"
                type="button"
                onClick={() => {
                  setLoader(true);
                  getCustomersList();
                }}
              >
                Load more...
              </button>
            </div>
          )}
        </tbody>
      </Table>
      <Dialog
        open={!!customerToDelete}
        onClose={() => setCustomerToDelete(null)}
        aria-labelledby="delete-customer-dialog"
        aria-describedby="delete-customer-description"
      >
        <div className="confirm-popup">
          <div className="header">
            <span className="title">Delete customer</span>
            <CancelSvg className="closeBtn" onClick={() => setCustomerToDelete(null)} />
          </div>
          <div className="message" id="delete-customer-description">
            {`Do you really want to delete customer "${customerToDelete?.name ?? customerToDelete}"?`}
          </div>
          <div className="buttons-group">
            <ButtonComponent
              className="danger"
              iconLeft={<DeleteIcon />}
              label="Yes"
              handleButton={() => {
                if (customerToDelete) handleDelete(customerToDelete ?? {});
                setCustomerToDelete(null);
              }}
            />
            <ButtonComponent
              iconLeft={<CancelSvg />}
              label="No"
              handleButton={() => setCustomerToDelete(null)}
            />
          </div>
        </div>
      </Dialog>
      {canManage && (
        <div className="donors-footer">
          <NewCustomer
            name={customerName}
            setName={setCustomerName}
            setLoader={setLoader}
            getCustomersList={getCustomersList}
          />
        </div>
      )}
    </div>
  );
};
